import { ErpatApiError } from '@erpat/sdk';
import { API_BASE_URL } from '@/utils/env';
import {
  erpatClient,
  getSessionEpoch,
  onSessionCancel,
  SESSION_CANCELLED_MESSAGE,
} from '@/services/api/erpat-client';
import {
  refreshSession,
  expireAfterFailedRefresh,
  isSessionCancelled,
  errorMessage,
  asRecord,
  type Json,
} from '@/services/api/http';

/**
 * Server-Sent-Events transport for the AI assistant chat.
 *
 * The `@erpat/sdk` client can't stream — its `request()` buffers the whole body
 * via `response.text()` and hard-aborts at 30s — so a persisted, token-by-token
 * chat needs its own fetch. This talks to the SAME stateful endpoints the SDK
 * uses, but with `{ "stream": true }`, so the backend replies over SSE and the
 * connection stays alive during a long generation (no 30s abort).
 *
 * Wire format the backend emits (see erpat7 Assistant::streamCompletion):
 *   `: open`                                     — heartbeat comment (ignored)
 *   `data: {"conversation_id": N}`               — in-band id (esp. a NEW thread)
 *   `data: {"choices":[{"delta":{"content":…}}]}` — OpenAI-style token deltas
 *   `data: {"ok":false,"error":{…}}`             — an in-band failure envelope
 *   `data: [DONE]`                               — terminal marker
 *
 * Auth mirrors the SDK: Bearer access token + `X-Company-Key`, with a single
 * reactive refresh-and-retry on 401. A pre-stream failure (permit/validation/
 * method) arrives as a normal JSON envelope (non-2xx), surfaced as an
 * {@link ErpatApiError} just like the SDK.
 */

export interface AssistantStreamTarget {
  /** Existing conversation id → append a message; omitted → create a new thread. */
  conversationId?: string;
  prompt: string;
}

export interface AssistantStreamHandlers {
  /** The conversation id, learned in-band (fires once, before any token). */
  onMeta?: (conversationId: string) => void;
  /** A reply text delta as it streams. */
  onToken?: (delta: string) => void;
}

export interface AssistantStreamResult {
  /** The conversation the reply belongs to (from `onMeta`, or the target id). */
  conversationId: string | null;
  /** The full reassembled reply text. */
  text: string;
}

// Abort the stream only after this long with NO bytes flowing — a stall guard,
// not a total cap (the timer resets on every chunk, so a long healthy
// generation is never killed). Generous, to cover a slow first token after the
// connection opens.
const STREAM_STALL_MS = 120_000;

/**
 * Stream an assistant reply. Resolves on the terminal `[DONE]` with the full
 * text + conversation id; throws an {@link ErpatApiError} on an in-band error
 * frame, a non-2xx pre-stream response, a network drop, a stall, or an abort.
 */
export function streamAssistant(
  target: AssistantStreamTarget,
  handlers: AssistantStreamHandlers,
  signal?: AbortSignal,
): Promise<AssistantStreamResult> {
  return runStream(target, handlers, signal, true);
}

async function runStream(
  target: AssistantStreamTarget,
  handlers: AssistantStreamHandlers,
  externalSignal: AbortSignal | undefined,
  allowRefresh: boolean,
): Promise<AssistantStreamResult> {
  const path = target.conversationId
    ? `/v1/api/me/assistant/conversations/${encodeURIComponent(target.conversationId)}/messages`
    : '/v1/api/me/assistant/conversations';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = erpatClient.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const companyKey = erpatClient.getCompanyKey();
  if (companyKey) headers['X-Company-Key'] = companyKey;
  const epoch = getSessionEpoch();

  // Combine the caller's abort signal with a self-resetting stall watchdog.
  const controller = new AbortController();
  const onExternalAbort = (): void => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onExternalAbort, { once: true });
  }
  // A sign-out cuts the stream too — for the WHOLE read, not just until the
  // headers arrive (a reply can stream for a minute, holding a connection the
  // logout request needs).
  let cancelledBySession = false;
  const untrackSession = onSessionCancel(() => {
    cancelledBySession = true;
    controller.abort();
  });
  let stallTimer: number | undefined;
  const armStall = (): void => {
    if (stallTimer !== undefined) window.clearTimeout(stallTimer);
    stallTimer = window.setTimeout(() => controller.abort(), STREAM_STALL_MS);
  };
  const cleanup = (): void => {
    if (stallTimer !== undefined) window.clearTimeout(stallTimer);
    externalSignal?.removeEventListener('abort', onExternalAbort);
    untrackSession();
  };
  // Reported in the SDK's own shape for a cancellation, so `isSessionCancelled()`
  // recognises it and the page does not start polling for a reply.
  const fail = (err: unknown): ErpatApiError =>
    cancelledBySession ? new ErpatApiError('network_error', SESSION_CANCELLED_MESSAGE, 0) : asStreamError(err);

  armStall();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ body: target.prompt, stream: true }),
      signal: controller.signal,
    });
  } catch (err) {
    cleanup();
    throw fail(err);
  }

  // 401 → refresh the access token once and retry (mirrors the SDK's reactive
  // refresh). The refresh is single-flighted, so concurrent callers collapse.
  if (response.status === 401 && allowRefresh && erpatClient.getRefreshToken()) {
    cleanup();
    try {
      await refreshSession();
    } catch (err) {
      expireAfterFailedRefresh(err, epoch);
      // A sign-out cancelled the refresh: report the cancellation, not an expiry.
      if (isSessionCancelled(err)) throw err;
      // ERPat refused the refresh token and the session was torn down (the
      // teardown bumps the epoch): this really is an expiry.
      if (getSessionEpoch() !== epoch) {
        throw new ErpatApiError('expired_token', 'Your session has expired.', 401);
      }
      // The session SURVIVED: the refresh merely did not get through (no
      // network, a timeout, something in front of ERPat answering in its
      // place). Say THAT, in the refresh failure's own words, instead of
      // claiming an expiry. The status stays the stream's own 401 — the server
      // refused this prompt, so no reply can land — rather than the refresh
      // error's: the chat page reads a status 0 as "the reply may still arrive,
      // poll for it" and a 403/404 as "the assistant is not enabled for this
      // account", and neither is true here.
      throw new ErpatApiError('refresh_failed', errorMessage(err), 401);
    }
    return runStream(target, handlers, externalSignal, false);
  }

  // A pre-stream failure (permit/validation/method) is a JSON envelope, not SSE.
  if (!response.ok) {
    cleanup();
    throw await envelopeError(response);
  }
  if (!response.body) {
    cleanup();
    throw new ErpatApiError('stream_unsupported', 'Streaming is not available.', 0);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let conversationId: string | null = target.conversationId ?? null;
  let doneSeen = false;
  let frameError: ErpatApiError | null = null;

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      armStall();
      buffer += decoder.decode(value, { stream: true });
      // Dispatch every complete SSE event (blocks separated by a blank line).
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const data = sseData(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        if (data === null) continue; // comment / no data field
        if (data === '[DONE]') {
          doneSeen = true;
          continue;
        }
        let json: unknown;
        try {
          json = JSON.parse(data);
        } catch {
          continue;
        }
        const rec = asRecord(json);
        // In-band conversation id — fires once, before tokens (esp. a NEW thread).
        if (rec.conversation_id !== undefined && rec.conversation_id !== null) {
          conversationId = String(rec.conversation_id);
          handlers.onMeta?.(conversationId);
          continue;
        }
        // In-band failure envelope (AI unavailable / upstream error).
        if (rec.ok === false) {
          frameError = envelopeErrorFromRecord(rec);
          continue;
        }
        // OpenAI-style token delta.
        const delta = deltaContent(rec);
        if (delta) {
          text += delta;
          handlers.onToken?.(delta);
        }
      }
    }
  } catch (err) {
    cleanup();
    try {
      await reader.cancel();
    } catch {
      /* already closing */
    }
    throw fail(err);
  }
  cleanup();

  // An error frame wins even if a [DONE] followed it (the backend sends both).
  if (frameError) throw frameError;
  if (!doneSeen) throw new ErpatApiError('stream_incomplete', 'The stream ended unexpectedly.', 0);
  return { conversationId, text };
}

/**
 * Extract the joined `data:` payload of one SSE event block, or null when the
 * block is only comments / has no data field. Comment lines (`:` prefix, e.g.
 * the `: open` heartbeat) and blank lines are skipped.
 */
function sseData(block: string): string | null {
  const parts: string[] = [];
  for (const raw of block.split('\n')) {
    const line = raw.replace(/\r$/, '');
    if (line === '' || line.startsWith(':')) continue;
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    if (field !== 'data') continue;
    let val = idx === -1 ? '' : line.slice(idx + 1);
    if (val.startsWith(' ')) val = val.slice(1);
    parts.push(val);
  }
  return parts.length ? parts.join('\n') : null;
}

/** The `choices[0].delta.content` token from an OpenAI-style chunk (or ''). */
function deltaContent(rec: Json): string {
  const choices = rec.choices;
  if (!Array.isArray(choices) || choices.length === 0) return '';
  const delta = asRecord(asRecord(choices[0]).delta);
  return typeof delta.content === 'string' ? delta.content : '';
}

/** Normalise a thrown value (AbortError, network failure) to an ErpatApiError. */
function asStreamError(err: unknown): ErpatApiError {
  if (err instanceof ErpatApiError) return err;
  // AbortError (stall / external cancel) and network failures → status 0, which
  // the page treats as recoverable (poll for the reply that landed server-side).
  const message = err instanceof Error ? err.message : String(err);
  return new ErpatApiError('network_error', message, 0);
}

/** Build an ErpatApiError from a non-2xx JSON envelope response. */
async function envelopeError(response: Response): Promise<ErpatApiError> {
  let rec: Json = {};
  try {
    rec = asRecord(await response.json());
  } catch {
    /* non-JSON body */
  }
  const base = envelopeErrorFromRecord(rec);
  // Prefer the real HTTP status (the envelope may omit an upstream_status).
  return new ErpatApiError(base.code, base.message, response.status || base.status);
}

/** Build an ErpatApiError from a `{ ok:false, error:{code,message,details} }` record. */
function envelopeErrorFromRecord(rec: Json): ErpatApiError {
  const error = asRecord(rec.error);
  const code = typeof error.code === 'string' ? error.code : 'assistant_error';
  const message =
    typeof error.message === 'string' ? error.message : 'The assistant service is unavailable.';
  const details = asRecord(error.details);
  const status = typeof details.upstream_status === 'number' ? details.upstream_status : 0;
  return new ErpatApiError(code, message, status);
}
